import SecureTokenStorage from '../utils/tokenStorage';
import logger from '../utils/logger';
import { extractUser } from '../utils/apiResponse';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface User {
  id: string;
  email: string;
  username: string;
  role: string;
  phone?: string;
  state?: string;
  city?: string;
  bio?: string;
  website?: string;
  profileImage?: string;
  isOAuthUser?: boolean;
}

class AuthService {
  private static refreshPromise: Promise<AuthTokens> | null = null;
  private static getCurrentUserPromise: Promise<User> | null = null;
  private static cachedUser: User | null = null;
  private static cacheTimestamp = 0;
  private static readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private static readonly STORAGE_KEY = 'cached_user';
  private static readonly STORAGE_TIMESTAMP_KEY = 'cached_user_timestamp';

  private static getAuthHeaders() {
    const tokens = SecureTokenStorage.getTokens();
    return {
      'Content-Type': 'application/json',
      Authorization: tokens?.accessToken ? `Bearer ${tokens.accessToken}` : '',
    };
  }

  /**
   * Refresh access token using refresh token
   * Uses a lock mechanism to prevent concurrent refresh attempts
   */
  static async refreshToken(): Promise<AuthTokens> {
    // If refresh is already in progress, wait for it
    if (this.refreshPromise) {
      logger.log('[AuthService] Refresh already in progress, waiting...');
      return this.refreshPromise;
    }

    const tokens = SecureTokenStorage.getTokens();

    if (!tokens?.refreshToken) {
      logger.error('[AuthService] No refresh token available');
      throw new Error('No refresh token available');
    }

    logger.log('[AuthService] Starting token refresh...');

    this.refreshPromise = (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/v1/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken: tokens.refreshToken }),
        });

        const data = await response.json();

        if (!response.ok) {
          const errorMsg =
            data.error || data.message || 'Failed to refresh token';
          logger.error('[AuthService] Token refresh failed:', {
            status: response.status,
            error: errorMsg,
            data,
          });
          throw new Error(errorMsg);
        }

        const newTokens: AuthTokens = {
          accessToken: data.data.accessToken,
          refreshToken: tokens.refreshToken || '', // Keep the same refresh token
        };

        // Update stored tokens
        SecureTokenStorage.updateAccessToken(newTokens.accessToken);
        logger.log('[AuthService] Token refreshed successfully');

        return newTokens;
      } finally {
        // Clear the promise after completion
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Load user from localStorage if available and fresh
   */
  private static loadUserFromStorage(): User | null {
    try {
      const cachedData = localStorage.getItem(this.STORAGE_KEY);
      const timestampStr = localStorage.getItem(this.STORAGE_TIMESTAMP_KEY);

      if (!cachedData || !timestampStr) {
        return null;
      }

      const timestamp = parseInt(timestampStr, 10);
      const now = Date.now();

      // Check if cache is still valid
      if (now - timestamp < this.CACHE_TTL) {
        const user = JSON.parse(cachedData) as User;
        this.cachedUser = user;
        this.cacheTimestamp = timestamp;
        logger.log('[AuthService] Loaded user from localStorage');
        return user;
      } else {
        // Cache expired, remove it
        localStorage.removeItem(this.STORAGE_KEY);
        localStorage.removeItem(this.STORAGE_TIMESTAMP_KEY);
      }
    } catch (error) {
      logger.error('[AuthService] Error loading user from storage:', error);
      // Clear corrupted data
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.STORAGE_TIMESTAMP_KEY);
    }
    return null;
  }

  /**
   * Save user to localStorage
   */
  private static saveUserToStorage(user: User): void {
    try {
      const now = Date.now();
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(user));
      localStorage.setItem(this.STORAGE_TIMESTAMP_KEY, now.toString());
    } catch (error) {
      logger.error('[AuthService] Error saving user to storage:', error);
    }
  }

  /**
   * Get current user data
   * Uses request deduplication to prevent multiple simultaneous calls
   * Uses localStorage for persistence across page refreshes
   */
  static async getCurrentUser(forceRefresh = false): Promise<User> {
    // Check in-memory cache first
    const now = Date.now();
    if (
      !forceRefresh &&
      this.cachedUser &&
      now - this.cacheTimestamp < this.CACHE_TTL
    ) {
      logger.log('[AuthService] Returning in-memory cached user data');
      return this.cachedUser;
    }

    // Check localStorage if in-memory cache is empty or expired
    if (!forceRefresh && !this.cachedUser) {
      const storedUser = this.loadUserFromStorage();
      if (storedUser) {
        return storedUser;
      }
    }

    // If request is already in flight, wait for it
    if (this.getCurrentUserPromise) {
      logger.log(
        '[AuthService] getCurrentUser already in progress, waiting...'
      );
      return this.getCurrentUserPromise;
    }

    // Start new request
    this.getCurrentUserPromise = (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/v1/auth/me`, {
          headers: this.getAuthHeaders(),
        });

        const data = await response.json();

        // Only log full response for non-401 errors (401s are expected)
        if (response.ok || response.status !== 401) {
          logger.debug(
            '[AuthService] getCurrentUser raw response:',
            JSON.stringify(data, null, 2)
          );
        }

        if (!response.ok) {
          // Clear cache on error
          this.clearCache();

          // Don't retry on 429 (rate limit)
          if (response.status === 429) {
            const errorMsg =
              data.error ||
              data.message ||
              'Too many requests. Please try again later.';
            logger.error('[AuthService] Rate limited:', errorMsg);
            throw new Error(errorMsg);
          }

          const errorMsg =
            data.error || data.message || 'Failed to get user data';

          // 401 errors are expected when tokens are invalid/expired
          // Log as info instead of error to reduce noise
          if (response.status === 401) {
            logger.log(
              '[AuthService] getCurrentUser: Token invalid or expired'
            );
          } else {
            logger.error('[AuthService] getCurrentUser failed:', {
              status: response.status,
              error: errorMsg,
            });
          }
          throw new Error(errorMsg);
        }

        // Use standardized user extraction
        const user = extractUser(data);

        // Cache the result in memory and localStorage
        this.cachedUser = user;
        this.cacheTimestamp = now;
        this.saveUserToStorage(user);

        logger.log('[AuthService] getCurrentUser success:', {
          id: user.id,
          email: user.email,
          hasProfileImage: !!user.profileImage,
        });
        return user;
      } finally {
        // Clear the promise after completion
        this.getCurrentUserPromise = null;
      }
    })();

    return this.getCurrentUserPromise;
  }

  /**
   * Check if tokens are valid and refresh if needed
   * Only refreshes on 401 errors, not on other errors
   * Returns user data if available to avoid duplicate calls
   */
  static async ensureValidToken(): Promise<{ isValid: boolean; user?: User }> {
    const tokens = SecureTokenStorage.getTokens();

    if (!tokens?.accessToken || !tokens?.refreshToken) {
      logger.log('[AuthService] No tokens found');
      return { isValid: false };
    }

    try {
      // Use getCurrentUser which has deduplication and caching
      // This avoids making duplicate requests
      const user = await this.getCurrentUser();
      logger.log('[AuthService] Token is valid');
      return { isValid: true, user };
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      // Handle 429 rate limit errors - don't treat as invalid token
      if (
        errorMessage.includes('Too many requests') ||
        errorMessage.includes('429')
      ) {
        logger.error('[AuthService] Rate limited during token check');
        // Return cached user if available, otherwise assume token is still valid
        if (this.cachedUser) {
          return { isValid: true, user: this.cachedUser };
        }
        // Assume token is valid but we're rate limited
        return { isValid: true };
      }

      // Check if it's a 401 error (unauthorized) - token expired or invalid
      if (
        errorMessage.includes('401') ||
        errorMessage.includes('Unauthorized') ||
        errorMessage.includes('Invalid token')
      ) {
        logger.log(
          '[AuthService] Token invalid or expired, attempting refresh...'
        );
        try {
          await this.refreshToken();
          logger.log(
            '[AuthService] Token refreshed successfully, verifying...'
          );

          // Clear cache before verifying
          this.clearCache();

          // Verify the new token works
          try {
            const user = await this.getCurrentUser();
            logger.log('[AuthService] Verified refreshed token is valid');
            return { isValid: true, user };
          } catch (verifyError) {
            // Refresh token is also invalid - user needs to log in again
            logger.log('[AuthService] Refresh token invalid, clearing tokens');
            SecureTokenStorage.clearTokens();
            this.clearCache();
            return { isValid: false };
          }
        } catch (refreshError) {
          // Refresh failed - tokens are invalid, clear them silently
          logger.log(
            '[AuthService] Token refresh failed, clearing invalid tokens'
          );
          SecureTokenStorage.clearTokens();
          this.clearCache();
          return { isValid: false };
        }
      }

      // For other errors, log but don't refresh
      logger.error('[AuthService] Token validation error:', error);

      // For network errors, assume token is still valid
      if (errorMessage.includes('Network') || errorMessage.includes('fetch')) {
        logger.log(
          '[AuthService] Network error, assuming token is still valid'
        );
        // Return cached user if available
        if (this.cachedUser) {
          return { isValid: true, user: this.cachedUser };
        }
        return { isValid: true };
      }

      // For other errors, check if we have cached user
      if (this.cachedUser) {
        logger.log('[AuthService] Using cached user despite error');
        return { isValid: true, user: this.cachedUser };
      }

      return { isValid: false };
    }
  }

  /**
   * Get cached user synchronously (for instant display on mount)
   * Returns null if no valid cache exists
   */
  static getCurrentUserSync(): User | null {
    // Check in-memory cache first
    const now = Date.now();
    if (this.cachedUser && now - this.cacheTimestamp < this.CACHE_TTL) {
      return this.cachedUser;
    }

    // Check localStorage
    return this.loadUserFromStorage();
  }

  /**
   * Update cache with user data (useful after profile updates)
   * This avoids an extra API call when we already have fresh user data
   */
  static updateCache(user: User): void {
    const now = Date.now();
    this.cachedUser = user;
    this.cacheTimestamp = now;
    this.saveUserToStorage(user);
    logger.log('[AuthService] Cache updated with new user data');
  }

  /**
   * Clear cached user data (e.g., on logout or profile update)
   * Clears both in-memory and localStorage cache
   */
  static clearCache(): void {
    this.cachedUser = null;
    this.cacheTimestamp = 0;
    this.getCurrentUserPromise = null;
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.STORAGE_TIMESTAMP_KEY);
    } catch (error) {
      logger.error('[AuthService] Error clearing storage cache:', error);
    }
  }

  /**
   * Logout user
   */
  static async logout(): Promise<void> {
    const tokens = SecureTokenStorage.getTokens();

    if (tokens?.refreshToken) {
      try {
        await fetch(`${API_BASE_URL}/v1/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken: tokens.refreshToken }),
        });
      } catch (error) {
        logger.error('Error during logout:', error);
      }
    }

    // Clear local storage
    SecureTokenStorage.clearTokens();
    // Clear cached user data
    this.clearCache();
  }
}

export default AuthService;
